import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  useToast,
  Spinner,
  Center,
  useBreakpointValue,
  Image,
  Grid,
  IconButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { ArrowBackIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons'
import DefaultLayout from '../components/layouts/DefaultLayout'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { createCommunityPost, uploadImage } from '../services/apiService'

interface CommunityPost {
  id: number
  user_id: number
  title: string
  content: string
  view_count: number
  like_count: number
  dislike_count: number
  comment_count: number
  status: string
  category?: 'FREE' | 'PROFIT'
  image_urls?: string[]
  created_at: string
  updated_at: string
  user: {
    id: number
    username: string
    profile_image_url?: string
  }
}

const CommunityWrite = () => {
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, md: false })
  
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState<'FREE' | 'PROFIT'>('FREE')
  const [images, setImages] = useState<string[]>([])
  const [uploadingImage, setUploadingImage] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // 로그인 체크
  useEffect(() => {
    if (!auth.id) {
      toast({
        title: '로그인 필요',
        description: '글을 작성하려면 로그인이 필요합니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      navigate('/community')
    }
  }, [auth.id, navigate, toast])

  // 이미지 업로드
  const handleImageUpload = async (file: File) => {
    try {
      setUploadingImage(true)
      const response = await uploadImage(file)
      setImages(prev => [...prev, response.data.url])
    } catch (error) {
      toast({
        title: '이미지 업로드 실패',
        description: '이미지 업로드에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setUploadingImage(false)
    }
  }

  // 이미지 삭제
  const handleImageRemove = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index))
  }

  // 이미지 업로드 클릭
  const handleImageUploadClick = () => {
    if (images.length >= 3) {
      toast({
        title: '이미지 개수 초과',
        description: '최대 3장까지만 업로드할 수 있습니다.',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        handleImageUpload(file)
      }
    }
    input.click()
  }

  // 글 작성
  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({
        title: '제목을 입력해주세요',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    if (!content.trim()) {
      toast({
        title: '내용을 입력해주세요',
        status: 'warning',
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      setSubmitting(true)
      await createCommunityPost({
        title,
        content,
        category,
        image_urls: images.length > 0 ? images : undefined
      })
      
      toast({
        title: '글 작성 완료',
        description: '게시글이 작성되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true
      })
      
      navigate('/community')
    } catch (error) {
      toast({
        title: '글 작성 실패',
        description: '게시글 작성에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setSubmitting(false)
    }
  }

  // ReactQuill 모듈 설정
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link'],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ],
    clipboard: { matchVisual: false }
  }

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'align', 'link', 'color', 'background'
  ]

  if (!auth.id) {
    return (
      <DefaultLayout>
        <Center py={20}>
          <Spinner size="xl" />
        </Center>
      </DefaultLayout>
    )
  }

  return (
    <DefaultLayout>
      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        {/* 헤더 */}
        <Flex justify="space-between" align="center" mb={6}>
          <HStack>
            <Button
              leftIcon={<ArrowBackIcon />}
              variant="ghost"
              onClick={() => navigate('/community')}
            >
              뒤로가기
            </Button>
            <Heading as="h1" size="lg">
              글 작성
            </Heading>
          </HStack>
        </Flex>

        {/* 폼 */}
        <Box
          bg="white"
          borderRadius="lg"
          border="1px"
          borderColor="gray.200"
          p={6}
        >
          <VStack spacing={6} align="stretch">
            {/* 카테고리 */}
            <FormControl>
              <FormLabel>카테고리</FormLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value as 'FREE' | 'PROFIT')}
                borderColor="blue.300"
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
              >
                <option value="FREE">💬 자유게시판</option>
                <option value="PROFIT">💰 수익인증</option>
              </Select>
              
              {/* 수익인증 카테고리 안내 */}
              <Alert status="info" mt={3} borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle fontSize="sm">💡 카테고리 선택 안내</AlertTitle>
                  <AlertDescription fontSize="xs" mt={1}>
                    <Text mb={1}>• <strong>자유게시판</strong>: 일반적인 소통과 정보 공유</Text>
                    <Text mb={1}>• <strong>수익인증</strong>: 실제 수익 증명 및 인증 (포인트 지급 대상)</Text>
                    <Text color="red.500" fontWeight="semibold">
                      ⚠️ 수익인증 게시글은 자유게시판에 올리면 포인트 지급이 되지 않습니다!
                    </Text>
                  </AlertDescription>
                </Box>
              </Alert>
            </FormControl>

            {/* 제목 */}
            <FormControl>
              <FormLabel>제목</FormLabel>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="제목을 입력하세요"
                borderColor="blue.300"
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
              />
            </FormControl>

            {/* 이미지 업로드 */}
            <FormControl>
              <FormLabel>이미지 (최대 3장)</FormLabel>
              <Box>
                <Grid templateColumns="repeat(auto-fill, minmax(120px, 1fr))" gap={3} mb={3}>
                  {images.map((imageUrl, index) => (
                    <Box key={index} position="relative">
                      <Image
                        src={imageUrl}
                        alt={`업로드된 이미지 ${index + 1}`}
                        borderRadius="md"
                        boxSize="120px"
                        objectFit="cover"
                        border="1px solid"
                        borderColor="gray.200"
                      />
                      <IconButton
                        aria-label="이미지 삭제"
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="solid"
                        position="absolute"
                        top={1}
                        right={1}
                        onClick={() => handleImageRemove(index)}
                      />
                    </Box>
                  ))}
                </Grid>
                
                {images.length < 3 && (
                  <Button
                    leftIcon={<AddIcon />}
                    onClick={handleImageUploadClick}
                    isLoading={uploadingImage}
                    loadingText="업로드 중..."
                    variant="outline"
                    colorScheme="blue"
                    size="sm"
                  >
                    이미지 추가 ({images.length}/3)
                  </Button>
                )}
              </Box>
            </FormControl>

            {/* 내용 */}
            <FormControl>
              <FormLabel>내용</FormLabel>
              <Box
                sx={{
                  '.ql-container': {
                    border: '1px solid #e2e8f0',
                    borderRadius: '0 0 6px 6px',
                    maxHeight: isMobile ? '300px' : '350px',
                    overflowY: 'auto'
                  },
                  '.ql-toolbar': {
                    border: '1px solid #e2e8f0',
                    borderTopLeftRadius: '6px',
                    borderTopRightRadius: '6px',
                    borderBottom: 'none'
                  },
                  '.ql-editor': {
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '12px',
                    minHeight: isMobile ? '150px' : '200px',
                    maxHeight: isMobile ? '250px' : '300px',
                    overflowY: 'auto',
                    '& img': {
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                      margin: '10px auto',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }
                  }
                }}
              >
                <ReactQuill
                  value={content}
                  onChange={setContent}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="내용을 입력하세요"
                  style={{ backgroundColor: 'white' }}
                />
              </Box>
            </FormControl>

            {/* 버튼 */}
            <HStack justify="flex-end" spacing={3} pt={4}>
              <Button
                variant="outline"
                onClick={() => navigate('/community')}
                size="md"
              >
                취소
              </Button>
              <Button
                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                color="white"
                onClick={handleSubmit}
                isLoading={submitting}
                loadingText="작성 중..."
                size="md"
                px={8}
                borderRadius="lg"
                boxShadow="0 4px 15px rgba(102, 126, 234, 0.3)"
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                  bg: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
                }}
                _active={{
                  transform: 'translateY(0)',
                  boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)'
                }}
                transition="all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
              >
                ✍️ 글 작성
              </Button>
            </HStack>
          </VStack>
        </Box>
      </Box>
    </DefaultLayout>
  )
}

export default CommunityWrite
